/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useContext } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

// @mui material components
import Card from '@mui/material/Card'
import Badge from '@mui/material/Badge'
import Modal from '@mui/material/Modal'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Footer from 'examples/Footer'
import MDButton from 'components/MDButton'
import MDAlert from 'components/MDAlert'
import { Tooltip, IconButton } from '@mui/material'

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import CrudService from 'services/cruds-service'
import { AbilityContext } from 'Can'
import { useAbility } from '@casl/react'
import GoogleMapComponent from 'examples/Maps'
import { Visibility } from '@mui/icons-material'
import { AuthContext } from 'context'
import { DataGrid } from '@mui/x-data-grid'
import MDInput from 'components/MDInput'

const DEVICE_LIST_STORAGE_KEY = "rivio.deviceList.tableSettings.v1";

const DEFAULT_DEVICE_LIST_TABLE_SETTINGS = {
  paginationModel: { page: 0, pageSize: 100 },
  sortModel: [{ field: "created_at", sort: "desc" }],
  filterModel: { items: [] },
  columnVisibilityModel: {
    device_id: true,
    status: true,
    icon: true,
    identifier: true,
    name: true,
    entity: true,
    entityAddressItemName: true,
    address: true,
    city: true,
    state: true,
    postal_code: true,
    item: true,
    type: true,
    brand: true,
    model: true,
    view_on_map: true,
    action: true,
  },
  searchQuery: "",
};

function readDeviceListTableSettings() {
  try {
    return {
      ...DEFAULT_DEVICE_LIST_TABLE_SETTINGS,
      ...JSON.parse(localStorage.getItem(DEVICE_LIST_STORAGE_KEY) || "{}"),
    };
  } catch (error) {
    console.error("readDeviceListTableSettings", error);
    return DEFAULT_DEVICE_LIST_TABLE_SETTINGS;
  }
}

function writeDeviceListTableSettings(nextSettings) {
  localStorage.setItem(DEVICE_LIST_STORAGE_KEY, JSON.stringify(nextSettings));
}

function InternalDeviceManagement() {
  let { state } = useLocation()
  const ability = useAbility(AbilityContext)
  const authContext = useContext(AuthContext)
  const [data, setData] = useState([])
  const [tableData, setTableData] = useState([])
  const [notification, setNotification] = useState({
    value: false,
    text: '',
    bgColor: 'info',
  })
  const [openModal, setOpenModal] = useState(false)
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [loader, setLoader] = useState(false)
  
  const savedTableSettings = readDeviceListTableSettings();
  const [paginationModel, setPaginationModel] = useState(savedTableSettings.paginationModel);
  const [sortModel, setSortModel] = useState(savedTableSettings.sortModel);
  const [filterModel, setFilterModel] = useState(savedTableSettings.filterModel);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(savedTableSettings.columnVisibilityModel);
  const [searchQuery, setSearchQuery] = useState(savedTableSettings.searchQuery);
  const [rowCount, setRowCount] = useState(0);

  const navigate = useNavigate()

  const fetchData = async () => {
    setLoader(true);
    try {
      setData([])
      const sortField = sortModel[0]?.field || "created_at";
      const sortOrder = sortModel[0]?.sort || "desc";

      const response = await CrudService.fetchDevicesList({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        sortField,
        sortOrder,
        search: searchQuery.trim().toLowerCase(),
        filters: filterModel.items,
      });
      if (response.data) {
        setRowCount(response?.total);
        // setTableData(getRows(response.data));
        setData(response.data)
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(fetchData, 500);
    return () => clearTimeout(timeout);
  }, [paginationModel, sortModel, filterModel, searchQuery]);

  useEffect(() => {
    if (!state) return
    setNotification({
      value: state.value,
      text: state.text,
    })
  }, [state])

  useEffect(() => {
    setTableData(getRows(data))
  }, [data])

  useEffect(() => {
    writeDeviceListTableSettings({
      paginationModel,
      sortModel,
      filterModel,
      columnVisibilityModel,
      searchQuery,
    });
  }, [paginationModel, sortModel, filterModel, columnVisibilityModel, searchQuery]);

  useEffect(() => {
    if (notification.value === true) {
      let timer = setTimeout(() => {
        setNotification({
          value: false,
          text: '',
        })
      }, 8000)
    }
  }, [notification])

  const clickAddHandler = () => {
    navigate('/devices/new-device')
  }

  const clickViewHandler = (id) => {
    navigate(`/devices/view-device/${id}`)
  }

  const clickEditHandler = (id) => {
    navigate(`/devices/edit-device/${id}`)
  }

  const clickDeleteHandler = async (e, id) => {
    try {
      if (!confirm('Are you sure you want to delete this device?')) {
        e.nativeEvent.stopImmediatePropagation()
      } else {
        const result = await CrudService.deleteDevice(id)
        setNotification({
          value: true,
          text: result.message,
          bgColor:
            result.status == '201' || result.status == '500' ? 'error' : 'info',
        })

        // the delete does not send a response
        // so I need to get again the categories to set it and this way the table gets updated -> it goes to the useEffect with data dependecy
        setLoader(true)
        const response = await CrudService.getInternalDevices()
        setData(response.data)
        setLoader(false)
      }
    } catch (err) {
      // it sends error is the device is associated with an device
      console.error(err)
      if (err.hasOwnProperty('errors')) {
        setNotification({
          value: true,
          text: err.errors[0].detail,
        })
      }
      return null
    }
  }

  const handleMapButtonClick = (row) => {
    const {
      street_address1,
      street_address2,
      city,
      state,
      country,
      postal_code,
      latitude,
      longitude,
    } = row
    setLatitude(latitude)
    setLongitude(longitude)
    // setMapUrl(url);
    setOpenModal(true)
  }

  function checkAccess(id, type) {
    if (authContext.userDetail.role_id == 6 && authContext.accessList.writeAccess.includes(id)) {
      return true
    }

    if (authContext.userDetail.role_id !== 6 && ability.can(type, 'devices')) {
      return true
    }

    return false
  }

  const searchHandler = (value) => {
    setSearchQuery(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }

  const getRows = (info) => {
    let updatedInfo = info.map((row) => {
      let entityName;
      if (row.attributes?.Entity) {
        if (row.attributes?.Entity?.name) {
          entityName = row.attributes?.Entity?.name;
        } else {
          entityName = row.attributes?.Entity?.first_name + ' ' + row.attributes?.Entity?.last_name;
        }
      } else {
        if (row.attributes?.EntityAddressItem?.Entity?.name)
          entityName = row.attributes?.EntityAddressItem?.Entity?.name
        else if (row.attributes?.EntityAddressItem?.Entity?.first_name)
          entityName =
            row.attributes?.EntityAddressItem?.Entity?.first_name +
            ' ' +
            row.attributes?.EntityAddressItem?.Entity?.last_name
      }

      const entityAddressItem = row.attributes?.EntityAddressItem;

      let color = row?.status_type_map_id === 19 ? 'success' : 'info'
      let variant = row?.status_type_map_id === 19 ? 'gradient' : 'contained'
      let badgeContent =
        row?.status_type_map_id === 19 ? 'Mapped' : 'Not Mapped'
      let identifier = row.attributes?.ExternalDeviceMapping?.external_device_id ?? '';

      return {
        id: row.id,
        name: row.attributes?.name,
        type: row.attributes.DeviceType?.type_name,
        entity: entityName,
        entityAddressItemName: entityAddressItem?.name ?? "--",
        item: entityAddressItem?.Item?.name,
        brand: row.attributes.DeviceBrand?.brand_name,
        identifier: identifier,
        model: row.attributes.DeviceModel?.model_name,
        createdAt: row.attributes.createdAt,
        updatedAt: row.attributes.updatedAt,
        // entityaddressitem: row.attributes.EntityAddressItem?.name,
        address: row.attributes.EntityAddressItem?.Address?.street_address1 ?? row.attributes?.Entity?.location?.Address?.street_address1,
        city: row.attributes.EntityAddressItem?.Address?.city ?? row.attributes?.Entity?.location?.Address?.city,
        state: row.attributes.EntityAddressItem?.Address?.state ?? row.attributes?.Entity?.location?.Address?.state,
        country: row.attributes.EntityAddressItem?.Address?.country ?? row.attributes?.Entity?.location?.Address?.country,
        postal_code: row.attributes.EntityAddressItem?.Address?.postal_code ?? row.attributes?.Entity?.location?.Address?.postal_code,
        latitude: row.attributes.EntityAddressItem?.Address?.latitude ?? row.attributes?.Entity?.location?.Address?.latitude,
        longitude: row.attributes.EntityAddressItem?.Address?.longitude ?? row.attributes?.Entity?.location?.Address?.longitude,
        icon: row.attributes.EntityAddressItem?.Icon?.class_name ?? '',
        status: {
          color: row?.attributes?.StatusOption?.BadgeDetail?.color || 'primary',
          variant: row?.attributes?.StatusOption?.BadgeDetail?.variant,
          badgeContent: row?.attributes?.StatusOption?.StatusType?.status_name,
          title: row?.attributes?.StatusOption?.StatusType?.description
        },
      }
    })
    return updatedInfo
  }

  const columns = [
    { headerName: 'ID', field: 'device_id', width: 70, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.id}</MDTypography> },
    {
      headerName: 'Status', field: 'status', sortable: false, renderCell: (info) => {
        return (
          <Badge
            color={info.value.color}
            variant={info.value.variant}
            badgeContent={info.value.badgeContent}
            style={{ marginLeft: '30px' }}
            title={info.value.title}
          ></Badge>
        )
      }
    },
    {
      headerName: 'Icon', field: 'icon', sortable: false, renderCell: (info) => {
        return (info.value ? <i className={info.value} style={{ fontSize: "24px" }}></i> : '')
      }
    },
    { headerName: 'Identifier', field: 'ExternalDeviceMapping.external_device_id', width: 150, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.identifier}</MDTypography> },
    { headerName: 'Name', field: 'name', width: 200, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.value}</MDTypography> },
    { headerName: 'Entity Name', field: 'Entity.name', width: 200, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.entity}</MDTypography> },
    {
      headerName: 'Entity Address Item',
      field: 'entityAddressItemName',
      width: 220,
      renderCell: (info) => (
        <Tooltip title={info.value || ""} arrow>
          <MDTypography component="div" fontSize={0.8} noWrap sx={{ maxWidth: 200 }}>
            {info.value || "--"}
          </MDTypography>
        </Tooltip>
      ),
    },
    { headerName: 'Address 1', field: 'EntityAddressItem.Address.street_address1', width: 250, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.address}</MDTypography> },
    { headerName: 'City', field: 'EntityAddressItem.Address.city', width: 150, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.city}</MDTypography> },
    { headerName: 'State', field: 'EntityAddressItem.Address.state', renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.state}</MDTypography> },
    { headerName: 'Postal Code', field: 'EntityAddressItem.Address.postal_code', renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.postal_code}</MDTypography> },
    { headerName: 'Item Name', field: 'EntityAddressItem.Item.name', width: 200, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.item}</MDTypography> },
    { headerName: 'Type', field: 'DeviceType.type_name', width: 120, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.type}</MDTypography> },
    { headerName: 'Brand', field: 'DeviceBrand.brand_name', renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.brand}</MDTypography> },
    { headerName: 'Model', field: 'DeviceModel.model_name', width: 150, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.model}</MDTypography> },
    {
      headerName: 'View on Map', field: 'view_on_map', sortable: false, renderCell: (info) => (
        <MDButton
          onClick={(e) => {
            e.stopPropagation();
            handleMapButtonClick(info.row)
          }}
        >
          View
        </MDButton>
      )
    },
    {
      headerName: 'Actions', field: 'action', sortable: false, width: 150, renderCell: (info) => (
        <MDBox display="flex" alignItems="center">
          {/* {checkAccess(info.row.id, 'view') && ( */}
            <Tooltip title="View Device">
              <IconButton
                onClick={() => clickViewHandler(info.row.id)}
              >
                <MDTypography>
                  <Visibility />
                </MDTypography>
              </IconButton>
            </Tooltip>
          {/* )} */}
          {checkAccess(info.row.id, 'edit') && (
            <Tooltip title="Edit Device">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  clickEditHandler(info.row.id)
                }}
              >
                <MDTypography>
                  <EditIcon />
                </MDTypography>
              </IconButton>
            </Tooltip>
          )}
          {checkAccess(info.row.id, 'delete') && (
            <Tooltip title="Delete Device">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  clickDeleteHandler(e, info.row.id)
                }
                }
              >
                <MDTypography>
                  <DeleteIcon />
                </MDTypography>
              </IconButton>
            </Tooltip>
          )}
        </MDBox>
      )
    }
    // { headerName: 'ID', field: 'id', width: 70 },
    // { headerName: 'Device ID', field: 'device_id', width: 200 },
    // { headerName: 'External Device ID', field: 'Device.ExternalDeviceMapping.external_device_id', width: 200 },
    // { headerName: 'Logs', field: 'error', width: 400 },
    // {
    //   headerName: 'Created Date',
    //   field: 'created_at',
    //   width: 300,
    //   isSortedDesc: true,
    //   isSorted: true,
    //   renderCell: (info) => {
    //     return (
    //       <div
    //         style={{
    //           whiteSpace: 'normal',
    //           maxWidth: '300px',
    //           wordWrap: 'break-word',
    //           wordBreak: 'break-word',
    //         }}
    //       >
    //         {moment(info.value).format(
    //           'MMMM DD, YYYY h:mm:ss A',
    //         )}
    //       </div>
    //     )
    //   },
    // },
  ]

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {notification.value && (
        <MDAlert
          color={notification.bgColor}
          my="20px"
          sx={{ position: 'sticky', top: '100px', zIndex: '1000' }}
        >
          <MDTypography variant="body2" color="white">
            {notification.text || ''}
          </MDTypography>
        </MDAlert>
      )}
      <MDBox pt={1}>
        <MDBox>
          <Card>
            <MDBox
              p={3}
              lineHeight={1}
              display="flex"
              justifyContent="space-between"
            >
              <MDTypography variant="h5" fontWeight="medium">
                Device Management
              </MDTypography>
              <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <MDBox tabIndex={-1}>
                  <GoogleMapComponent lat={latitude} lng={longitude} />
                </MDBox>
              </Modal>
              <MDBox display="flex" alignItems="center">
                {ability.can('create', 'devices') && (
                  <MDButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    type="submit"
                    onClick={clickAddHandler}
                  >
                    + Add Device
                  </MDButton>
                )}
                <MDInput
                  type="text"
                  label="Search"
                  variant="outlined"
                  onChange={(e) => searchHandler(e.target.value)}
                  value={searchQuery}
                />
              </MDBox>
            </MDBox>
            {/* <DataTable
              table={dataTableData}
              canSearch={true}
              loader={loader}
              onRowRedirect={(row) => {
                navigate(`/devices/view-device/${row}`);
              }}
              entriesPerPage={{ defaultValue: 100 }}
            /> */}
            
            <MDBox sx={{ height: `calc(100vh - 14rem)` }} >
              <DataGrid
                rows={tableData}
                columns={columns}
                pagination
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                sortModel={sortModel}
                filterModel={filterModel}
                columnVisibilityModel={columnVisibilityModel}
                pageSizeOptions={[25, 50, 100]}
                onSortModelChange={(model) => setSortModel(model)}
                onFilterModelChange={(model) => setFilterModel(model)}
                onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
                onRowSelectionModelChange={(e) => navigate(`/devices/view-device/${e}`)}
                onPaginationModelChange={(newPage) => setPaginationModel(newPage)}
                loading={loader}
                disableSelectionOnClick
                sx={{ color: '#fff' }}
                initialState={{ pinnedColumns: { left: ['ID'] } }}
              />
            </MDBox>
            {/* <MultiSelectDataTable rowData={getRows(data)} selectedRows={selectedRows} setSelectedRows={setSelectedRows}/> */}
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default InternalDeviceManagement
